import { breakpoints, Inline, Stack, Text, textColors, textVariants, vars } from '@etg/wings';
import { css, cx, getTextStyles, mediaQueries } from '@eti/styles';
import { CurrencyCircleDollar } from '@phosphor-icons/react';
import { Icon } from '@eti/components';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { price } from '@eti/utils';
import type { PossibleToAddProductType, SellSpecificationType } from '../../../../models';
import { getBaggageSpecificationDetails } from './utils';
import CheckedBaggageSelfTransferCard from './CheckedBaggageSelfTransferCard';

const possibleToAddBaggageStyles = (
  isIncludedSectionEnabled?: boolean,
  isInDrawer?: boolean,
  isResultCardBaggageEnabled?: boolean,
) => css`
  border-top: ${isIncludedSectionEnabled && isResultCardBaggageEnabled && isInDrawer
    ? `1px solid ${vars.colors.divider}`
    : 'none'};
  flex-grow: ${isIncludedSectionEnabled ? '2' : '3'};
  padding-top: ${isIncludedSectionEnabled && isResultCardBaggageEnabled && isInDrawer
    ? '16px'
    : '0'};
`;

const periodStyles = css`
  background: #737373;
  border-radius: 50%;
  display: inline-block;
  height: 3px;
  margin: 3px 5px;
  width: 3px;
`;

const possibleToAddBaggageTextStyles = css`
  margin-inline-start: 28px;
`;

const baggageSectionStyles = css`
  flex-basis: 0;
  flex-grow: 2;
  position: relative;

  @media (max-width: ${breakpoints._768}) {
    width: 100%;
  }
`;

const additionalBaggageInformationStyles = css`
  ${getTextStyles()};
  font-size: 0.75rem;
  padding-top: 5px;

  @media ${mediaQueries.large.up} {
    font-size: 0.875rem;
    padding-top: 0;
  }
`;
const baggageIconAndInfoSectionStyles = css`
  padding-inline-start: 24px;
`;

const iconWrapperStyles = css`
  background-color: ${vars.colors.signal.information.background};
  border-radius: 8px;
  padding: 4px;
`;

const infoIconStyles = css`
  position: absolute;
`;

const checkInBaggageInfoStyles = css`
  flex-grow: 2;
`;

export interface PossibleToAddBaggageProps {
  hasCheckInBaggage: boolean;
  isInDrawer: boolean;
  isIncludedSectionEnabled: boolean;
  isResultCardBaggageEnabled: boolean;
  isVI: boolean;
  possibleToAddBaggages: PossibleToAddProductType[];
}

const PossibleToAddBaggage = ({
  hasCheckInBaggage,
  isInDrawer,
  isIncludedSectionEnabled,
  isResultCardBaggageEnabled,
  isVI,
  possibleToAddBaggages,
}: PossibleToAddBaggageProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { market } = useSiteContext();
  const isVISelfTansferDealEnabled = p('IbeClient.VI.SelfTransferDeal.Enabled');
  const shouldShowBaggagePrice = p('Result.Page.Drawer.Baggage.Price.Enabled');

  /**
   * Sorting listOfPossibleToAdd array as per requirement to show cabin baggage first.
   * If api response returns the products in any order we should make sure the
   * requirement meets the expectation.
   */
  const sortedBaggages = possibleToAddBaggages.sort(
    (a, b) => Number(b.productId) - Number(a.productId),
  );

  const getBaggageDimensionsAriaLabel = (specification: SellSpecificationType) => {
    if (specification?.__typename !== 'SellCabinBaggageNew') {
      return '';
    }

    return `, ${t(
      'ScreenReader.ResultCard.TripBaggageInfo.BaggageDimensions.AriaLabel',
      specification?.size3d?.length,
      specification?.size3d?.width,
      specification?.size3d?.height,
    )}`;
  };

  const getBaggageWeightAriaLabel = (
    specification: SellSpecificationType,
    specificationDetails: ReturnType<typeof getBaggageSpecificationDetails>,
  ) => {
    if (specification?.__typename !== 'SellCabinBaggageNew') {
      return '';
    }

    return specificationDetails?.weight
      ? `, ${specificationDetails.weight} ${specificationDetails.weightUnit}`
      : '';
  };

  const getSortedBaggagesAriaLabels = () => {
    const allBaggagesAriaLabelsArray: string[] = [];

    sortedBaggages.forEach((itemCanBeAdded: PossibleToAddProductType) => {
      const { baggageText, sellSpecification } = itemCanBeAdded;
      const sellSpecificationDetails = getBaggageSpecificationDetails(sellSpecification);

      const baggagePriceAriaLabel = () => {
        if (!shouldShowBaggagePrice) {
          return '';
        }

        return `, ${t(
          'Result.Baggage.ExtraProductsPrice.From.Text',
        )} ${price.replaceWithCurrencyName(
          sellSpecificationDetails ? sellSpecificationDetails.startingFromPrice : '',
        )}`;
      };

      allBaggagesAriaLabelsArray.push(
        `, ${t(baggageText)}${getBaggageDimensionsAriaLabel(
          sellSpecification,
        )}${getBaggageWeightAriaLabel(
          sellSpecification,
          sellSpecificationDetails,
        )} ${baggagePriceAriaLabel()}`,
      );
    });

    return allBaggagesAriaLabelsArray.join(' ');
  };

  const allPossibleToAddBaggagesAriaLabels = `${t(
    'Result.Baggage.PossibleToAdd.Text',
  )} ${getSortedBaggagesAriaLabels()}`;

  return (
    <>
      <Stack
        aria-label={allPossibleToAddBaggagesAriaLabels}
        className={cx(
          baggageSectionStyles,
          possibleToAddBaggageStyles(
            isIncludedSectionEnabled,
            isInDrawer,
            isResultCardBaggageEnabled,
          ),
        )}
        data-testid="possible-to-add-section"
        spacing={8}
      >
        <Inline align="start" aria-hidden noWrap spacing={4}>
          <CurrencyCircleDollar color={vars.colors.text.primary} size="20" weight="fill" />
          <Text variant={textVariants._14_BOLD}>{t('Result.Baggage.PossibleToAdd.Text')}</Text>
        </Inline>
        {sortedBaggages.map((itemCanBeAdded) => {
          const { baggageIconId = '', baggageText, productId, sellSpecification } = itemCanBeAdded;
          const sellSpecificationDetails = getBaggageSpecificationDetails(sellSpecification);

          return (
            <Inline
              align="start"
              aria-hidden
              className={baggageIconAndInfoSectionStyles}
              data-testid={baggageIconId}
              key={productId}
              noWrap
              spacing={8}
            >
              <Icon
                className={isResultCardBaggageEnabled ? iconWrapperStyles : infoIconStyles}
                id={baggageIconId}
                size="20px"
              />
              <div
                className={
                  isResultCardBaggageEnabled
                    ? checkInBaggageInfoStyles
                    : cx(checkInBaggageInfoStyles, possibleToAddBaggageTextStyles)
                }
              >
                <Text>{t(baggageText)}</Text>
                <Inline alignY="center" noWrap spacing={4}>
                  {sellSpecification?.__typename === 'SellCabinBaggageNew' && (
                    <>
                      <Text color={textColors.SECONDARY} variant={textVariants._12}>
                        <bdi>
                          ({sellSpecification?.size3d?.length} x {sellSpecification?.size3d?.width}{' '}
                          x {sellSpecification?.size3d?.height})
                        </bdi>
                        <div className={periodStyles} />
                        <bdi>
                          {sellSpecificationDetails?.weight} {sellSpecificationDetails?.weightUnit}
                        </bdi>
                      </Text>
                    </>
                  )}
                </Inline>
              </div>
              {shouldShowBaggagePrice && (
                <Inline data-testid={`${itemCanBeAdded.name}-baggage-price`} spacing={4}>
                  <Text>{t('Result.Baggage.ExtraProductsPrice.From.Text')} </Text>
                  <Text>{sellSpecificationDetails?.startingFromPrice}</Text>
                </Inline>
              )}
            </Inline>
          );
        })}
        {market.code === 'US' && (
          <Text
            asHtml
            className={additionalBaggageInformationStyles}
            data-testid="special-market-link"
          >
            {t('Product.Baggage.NotIncluded.PriceSpecification.AdditionalInformation')}
          </Text>
        )}
      </Stack>
      {isVISelfTansferDealEnabled && isVI && hasCheckInBaggage && (
        <CheckedBaggageSelfTransferCard />
      )}
    </>
  );
};

export default PossibleToAddBaggage;
